import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  IconButton,
  Snackbar,
  Slide,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getCategoryProducts } from "./categoryServices"; // Import the function

function ImgByCat({ category, onBack }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null); // State to hold the clicked product
  const [openDialog, setOpenDialog] = useState(false); // State to control the dialog visibility
  const [openSnackbar, setOpenSnackbar] = useState(false); // Snackbar open state
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productList = await getCategoryProducts(category.id);

        setProducts(productList);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [category]);

  const handleCardClick = (product) => {
    setSelectedProduct(product);
    setOpenDialog(true); // Open the dialog on card click
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null); // Reset selected product when dialog is closed
  };

  // Function to copy text to clipboard
  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setSnackbarMessage("Copied to clipboard!");
        setOpenSnackbar(true);
        setTimeout(() => {
          setOpenSnackbar(false); // Close Snackbar after 2 seconds
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleImgDownload = async (imageUrl, fileName, fullPath) => {
    try {
      // Determine inferred filename
      const inferredName = fileName || (fullPath ? fullPath.split('/').pop() : decodeURIComponent((imageUrl.split('/').pop() || '').split('?')[0]));

      // Resolve storage path: prefer provided fullPath, else derive from token URL
      let path = fullPath;
      if (!path && imageUrl) {
        try {
          const match = imageUrl.match(/\/o\/(.*?)($|\?)/);
          if (match && match[1]) path = decodeURIComponent(match[1]);
        } catch (e) {
          // ignore
        }
      }

      // Cloud Function endpoints (primary proxy and fallback)
      const projectFuncBase = 'https://us-central1-officialimages-523a5.cloudfunctions.net';
      const primaryUrl = `${projectFuncBase}/downloadImage`;
      const fallbackUrl = `${projectFuncBase}/downloadImageFallback`;

      let proxyUrl;
      if (path) {
        proxyUrl = `${primaryUrl}?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(inferredName)}`;
      } else if (imageUrl) {
        // Use fallback that proxies the token URL server-side
        proxyUrl = `${fallbackUrl}?url=${encodeURIComponent(imageUrl)}&filename=${encodeURIComponent(inferredName)}`;
      } else {
        throw new Error('No path or URL available for download');
      }

      // Fetch from Cloud Function (server-side proxy avoids CORS issues)
      const resp = await fetch(proxyUrl, { method: 'GET', mode: 'cors' });
      if (!resp.ok) throw new Error(`Proxy fetch failed (${resp.status})`);
      const blob = await resp.blob();

      // Trigger download via blob URL without navigating
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = inferredName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading the image via proxy function:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        Products in {category.catName}
      </Typography>

      {/* Use a flexbox layout with product name, SKU on the left, and image on the right */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {products.length > 0 ? (
          products.map((product) => (
            <Tooltip title="Click to view details" key={product.id}>
              <Card
                key={product.id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  maxWidth: 600,
                  transition: "transform 0.3s ease", // Smooth transition for the hover effect
                  "&:hover": {
                    transform: "translateY(-5px)", // Moves the card up by 5px on hover
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)", // Optional: Adds a shadow effect on hover
                  },
                }}
                onClick={() => handleCardClick(product)} // Handle click to open dialog
              >
                <CardContent sx={{ flex: 1 }}>
                  {/* Product Name and SKU in a vertical stack */}
                  <Typography variant="h6">{product.productName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU: {product.sku}
                  </Typography>
                </CardContent>

                {/* Product Image on the right */}
                <div>
                  {Array.isArray(product.images) && product.images.length > 0 ? (
                    <img src={product.images[0]} alt={`${product.productName}`} style={{ width: "140px", height: "auto", marginLeft: "10px" }} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image available
                    </Typography>
                  )}
                </div>
              </Card>
            </Tooltip>
          ))
        ) : (
          <Typography>No products found for this category.</Typography>
        )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth={true} maxWidth="lg">
        {/* 🔴 Red Bar at the Top */}
        <Box sx={{ backgroundColor: "red", color: "white", padding: 2, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Viewing Details for {selectedProduct?.productName}
          </Typography>
        </Box>

        <DialogContent>
          {/* 🔽 Accordion for Instructions */}
          <Accordion sx={{ marginBottom: 2, backgroundColor: "#f5f5f5" }}>
            <Tooltip title="Click to expand/collapse" placement="top">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  Instructions
                </Typography>
              </AccordionSummary>
            </Tooltip>
            <AccordionDetails>
              <Typography variant="body2">
                Click on the <ContentCopyIcon sx={{ verticalAlign: "middle" }} /> icon to copy Product Name, SKU, or Description to the clipboard to
                paste into your project. <br />
                Click Download buttons under images to download them to your computer.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Typography variant="h6">
            {selectedProduct?.productName}
            <Tooltip title="Copy to Clipboard">
              <IconButton
                sx={{ marginLeft: 1 }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent dialog close when copying
                  handleCopy(selectedProduct?.productName);
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Typography>

          <Typography variant="body1">
            SKU: {selectedProduct?.sku}
            <Tooltip title="Copy to Clipboard">
              <IconButton
                sx={{ marginLeft: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(selectedProduct?.sku);
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Typography>

          <Typography variant="h6" sx={{ marginTop: 1 }}>
            Description:{" "}
            <Tooltip title="Copy to Clipboard">
              <IconButton
                sx={{ marginLeft: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(selectedProduct?.description);
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {selectedProduct?.description}
          </Typography>

          {selectedProduct?.images && selectedProduct.images.length > 0 ? (
            <Box sx={{ marginTop: 2 }}>
              {selectedProduct.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "inline-block",
                    textAlign: "center",
                    marginRight: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <img src={image} alt={`${selectedProduct.productName} ${index + 1}`} style={{ width: "200px", height: "auto" }} />
                  <Box>
                    <Tooltip title="Download Image">
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleImgDownload(
                            image,
                            `${selectedProduct.productName}_${index + 1}.jpg`,
                            // if images are stored as objects with {url, fileName, fullPath}, pass fullPath
                            selectedProduct.imagePaths && selectedProduct.imagePaths[index]
                          )
                        }
                      >
                        Download
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No images available
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        autoHideDuration={2000} // Automatically close after 2 seconds
        onClose={() => setOpenSnackbar(false)} // Close the Snackbar
        TransitionComponent={(props) => <Slide {...props} direction="down" />} // Slide down transition
        message={snackbarMessage}
      />
    </Box>
  );
}

export default ImgByCat;
